import { Box, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { adminApi } from '../../api/adminApi';
import { PageHeader } from '../../components/PageHeader';
import { COMPETITION_STATUS_LABELS, type CompetitionStatus } from '../../types/domain';
import { useImpersonation, withUnit } from '../../hooks/useImpersonation';
import { formatDateCz } from '../../utils/dateFormat';

const STATUS_COLOR: Record<CompetitionStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default',
  LIVE: 'error',
  FINISHED: 'success',
};

export const AdminDashboardPage = () => {
  const { effectiveUnitId, isImpersonating, impersonatedUnit } = useImpersonation();
  const { data: comps = [], isLoading } = useQuery({
    queryKey: ['admin', 'competitions', effectiveUnitId ?? 'all'],
    queryFn: () => adminApi.listCompetitions(effectiveUnitId),
  });

  const live = comps.filter((c) => c.status === 'LIVE');
  const draft = comps.filter((c) => c.status === 'DRAFT');
  const finished = comps.filter((c) => c.status === 'FINISHED');

  const Section = ({ title, list }: { title: string; list: typeof comps }) => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title} ({list.length})
        </Typography>
        {list.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            Žádná soutěž v této kategorii.
          </Typography>
        )}
        <Stack spacing={1}>
          {list.map((c) => (
            <Card
              key={c.id}
              component={RouterLink}
              to={withUnit(`/admin/competitions/${c.id}`, effectiveUnitId)}
              variant="outlined"
              sx={{ textDecoration: 'none', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateCz(c.competitionDate)} {c.location && `• ${c.location}`} • {c.unitName}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={COMPETITION_STATUS_LABELS[c.status]} color={STATUS_COLOR[c.status]} />
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {c.shortCode}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Dashboard | Admin jednotky</title>
      </Helmet>
      <PageHeader
        title="Přehled soutěží"
        subtitle={
          isImpersonating
            ? `Soutěže jednotky ${impersonatedUnit?.name ?? ''}`
            : 'Probíhající, nadcházející a dokončené soutěže vaší jednotky'
        }
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Section title="Probíhá" list={live} />
          <Section title="Rozpracované" list={draft} />
          <Section title="Dokončené" list={finished} />
        </>
      )}
    </>
  );
};
